import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useListQuery,
  useNavigateFromList,
  usePageClamp,
} from '@/hooks/useUrlListState';
import { listDefaults, nextSortDirection } from '@/utils/urlListState';
import {
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  DateRangeFilter,
  ColumnSettings,
  PrintLabelModal,
  ResetFiltersButton,
} from '../../../components';
import { useTableColumns } from '../../../hooks/useTableColumns';
import dayjs from 'dayjs';
import { InputSearch } from '../../../components/Input';
import { Status } from '../../../components/Status';
import { TableData } from '../../../components/Table';
import { showErrorToast } from '../../../components/toast/globalToast';
import { extractApiErrorMessage, formatRupiah } from '../../../utils';
import { vehiclesApi, Vehicle } from '../../../services/api/vehicles';
import Axios from '../../../utils/axios';
import { getListModel } from '../../../services/api/models';
import { getListOem } from '../../../services/api/oems';
import { getListPitstop, Pitstop } from '../../../services/api/pitstops';
import { getPurchaseOrders } from '../../../services/api/purchaseOrders';
import {
  getCompanyEntityLabel,
  PurchaseOrder,
} from '../../../types/purchase-order';
import { UniversalFilter } from '../../../components';
import {
  VehicleStatus,
  BUSINESS_UNIT_OPTIONS,
  getBusinessUnitLabel,
} from '../../../types/common';
import SummaryStat from './OwnedVehicle/SummaryStat';
import ReturnVehicleModal from './ReturnVehicle/ReturnVehicleModal';

// Date range interface for DateRangeFilter
interface DateRange {
  from: Date | null;
  to: Date | null;
}

// Status label mapper to convert uppercase enum values to user-friendly labels
const statusLabelMapper: Record<VehicleStatus, string> = {
  [VehicleStatus.IDLE]: 'Idle',
  [VehicleStatus.ACTIVE]: 'Active',
  [VehicleStatus.LOST]: 'Lost',
  [VehicleStatus.IN_REPAIR]: 'In Repair',
  [VehicleStatus.BROKEN]: 'Broken',
  [VehicleStatus.RETURNED]: 'Returned',
};

// Default statuses constant for consistency
const DEFAULT_STATUSES = 'IDLE,ACTIVE,LOST,IN_REPAIR,BROKEN';

// Dashboard type
type Dashboard = {
  totalActive: number;
  totalIdle: number;
  totalInRepair: number;
  totalBroken: number;
  totalLost: number;
  // Idle-exposure aggregates (currently-idle vehicles only)
  totalIdleDays: number;
  avgIdleDays: number;
  maxIdleDays: number;
  totalIdleCost: number;
};

// Vehicle metric types using Enum for type safety and scalability
enum VehicleMetric {
  TOTAL = 'TOTAL',
  ACTIVE = 'ACTIVE',
  IDLE = 'IDLE',
  IN_REPAIR = 'IN_REPAIR',
  BROKEN = 'BROKEN',
  LOST = 'LOST',
}

const OWNED_FILTER_KEYS = [
  'oemIDs',
  'modelIDs',
  'statuses',
  'contractTypes',
  'hubIDs',
  'businessUnits',
  'purchaseOrderIDs',
] as const;

const OWNED_LIST_DEFAULTS = listDefaults({
  sort: 'createdAt',
  dir: 'desc' as string | null,
  // The metric cards double as a status filter; TOTAL means every status.
  cards: [VehicleMetric.TOTAL] as string[],
  oemIDs: [] as string[],
  modelIDs: [] as string[],
  statuses: DEFAULT_STATUSES.split(','),
  contractTypes: [] as string[],
  hubIDs: [] as string[],
  businessUnits: [] as string[],
  purchaseOrderIDs: [] as string[],
});

const isVehicleMetric = (value: string) =>
  Object.values(VehicleMetric).includes(value as VehicleMetric);

const OWNED_LIST_VALIDATORS = {
  cards: (cards: string[]) => cards.every(isVehicleMetric),
};

// Metric configuration mapping - clean and maintainable
const METRIC_CONFIG = {
  [VehicleMetric.TOTAL]: {
    title: 'Total Vehicles',
    icon: 'ri-motorbike-line',
    getValue: (d: Dashboard) =>
      (d.totalActive || 0) +
      (d.totalIdle || 0) +
      (d.totalInRepair || 0) +
      (d.totalBroken || 0) +
      (d.totalLost || 0),
  },
  [VehicleMetric.ACTIVE]: {
    title: 'Active Vehicles',
    icon: 'ri-pulse-line',
    getValue: (d: Dashboard) => d.totalActive || 0,
  },
  [VehicleMetric.IDLE]: {
    title: 'Idle Vehicles',
    icon: 'ri-pause-line',
    getValue: (d: Dashboard) => d.totalIdle || 0,
  },
  [VehicleMetric.IN_REPAIR]: {
    title: 'In Repair',
    icon: 'ri-tools-line',
    getValue: (d: Dashboard) => d.totalInRepair || 0,
  },
  [VehicleMetric.BROKEN]: {
    title: 'Broken',
    icon: 'ri-alert-line',
    getValue: (d: Dashboard) => d.totalBroken || 0,
  },
  [VehicleMetric.LOST]: {
    title: 'Lost',
    icon: 'ri-close-line',
    getValue: (d: Dashboard) => d.totalLost || 0,
  },
} as const;

// Display order using Enum values
const METRIC_ORDER = [
  VehicleMetric.TOTAL,
  VehicleMetric.ACTIVE,
  VehicleMetric.IDLE,
  VehicleMetric.IN_REPAIR,
  VehicleMetric.BROKEN,
  VehicleMetric.LOST,
] as const;

// Clean summary cards component with click-to-filter functionality
const VehiclesSummaryCards: React.FC<{
  dashboard: Dashboard;
  activeCards: Set<VehicleMetric>;
  onCardClick: (metric: VehicleMetric) => void;
}> = ({ dashboard, activeCards, onCardClick }) => (
  <section aria-labelledby="vehicles-summary" className="mb-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
      {METRIC_ORDER.map((metric) => {
        const config = METRIC_CONFIG[metric];
        // The Idle card surfaces fleet-wide idle exposure below its count.
        const metrics =
          metric === VehicleMetric.IDLE
            ? [
                {
                  label: 'Max Idle Days',
                  value: `${dashboard.maxIdleDays.toLocaleString(
                    'en-US'
                  )} Days`,
                },
                {
                  label: 'Total Idle Cost',
                  value: formatRupiah(dashboard.totalIdleCost),
                },
              ]
            : undefined;
        // All cards are now clickable, including Total
        return (
          <SummaryStat
            key={metric}
            title={config.title}
            value={config.getValue(dashboard)}
            icon={<i className={config.icon} />}
            metrics={metrics}
            onClick={() => onCardClick(metric)}
            isActive={activeCards.has(metric)}
          />
        );
      })}
    </div>
  </section>
);

// Enum-based filter mapping for clean API integration
const VEHICLE_METRIC_TO_STATUS_MAP: Record<
  VehicleMetric,
  VehicleStatus | null
> = {
  [VehicleMetric.TOTAL]: null, // No filter for total
  [VehicleMetric.ACTIVE]: VehicleStatus.ACTIVE,
  [VehicleMetric.IDLE]: VehicleStatus.IDLE,
  [VehicleMetric.IN_REPAIR]: VehicleStatus.IN_REPAIR,
  [VehicleMetric.BROKEN]: VehicleStatus.BROKEN,
  [VehicleMetric.LOST]: VehicleStatus.LOST,
} as const;

// Reverse mapping for bidirectional sync - Status to Metric
const VEHICLE_STATUS_TO_METRIC_MAP: Record<VehicleStatus, VehicleMetric> = {
  [VehicleStatus.ACTIVE]: VehicleMetric.ACTIVE,
  [VehicleStatus.IDLE]: VehicleMetric.IDLE,
  [VehicleStatus.IN_REPAIR]: VehicleMetric.IN_REPAIR,
  [VehicleStatus.BROKEN]: VehicleMetric.BROKEN,
  [VehicleStatus.LOST]: VehicleMetric.LOST,
  [VehicleStatus.RETURNED]: VehicleMetric.TOTAL,
} as const;

interface OwnedVehicleTabProps {
  onAddVehicle: () => void;
  onTotalChange?: (total: number) => void;
  onEditVehicle?: (vehicle: Vehicle) => void;
  onDeleteVehicle?: (vehicle: Vehicle) => void;
  refreshTrigger?: number; // Add this to trigger refresh from parent
  onDownloadParamsChange?: (params: Record<string, any>) => void;
}

const OwnedVehicleTab: React.FC<OwnedVehicleTabProps> = ({
  onAddVehicle,
  onTotalChange,
  onEditVehicle,
  onDeleteVehicle,
  refreshTrigger,
  onDownloadParamsChange,
}) => {
  const navigate = useNavigateFromList();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);

  // Dashboard counts from backend API
  const [dashboard, setDashboard] = useState<Dashboard>({
    totalActive: 0,
    totalIdle: 0,
    totalInRepair: 0,
    totalBroken: 0,
    totalLost: 0,
    totalIdleDays: 0,
    avgIdleDays: 0,
    maxIdleDays: 0,
    totalIdleCost: 0,
  });

  // Search, filters, cards, sort and page live in the URL, so a refresh keeps them.
  const {
    state: listState,
    set: setListState,
    searchTerm,
    currentPage,
    pageSize: itemsPerPage,
    sortField: sortFieldParam,
    sortDirection: sortDirectionParam,
    activeFilters,
    dateRange,
    datePickerSeed,
    controlsKey,
    searchInput,
    setSearchInput,
    setSearchTerm,
    setCurrentPage,
    setFilters: setActiveFilters,
    setDateRange,
    setSort,
    setPageSize: setItemsPerPage,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(
    OWNED_LIST_DEFAULTS,
    OWNED_FILTER_KEYS,
    OWNED_LIST_VALIDATORS
  );
  const sortField = sortFieldParam as keyof Vehicle;
  const sortDirection = sortDirectionParam ?? 'desc';
  const [totalItems, setTotalItems] = useState(0);
  // Active cards state for click-to-filter functionality (support multiple)
  const activeCards = useMemo(
    () => new Set(listState.cards as VehicleMetric[]),
    [listState.cards]
  );
  const setActiveCards = useCallback(
    (cards: Set<VehicleMetric>) => setListState({ cards: Array.from(cards) }),
    [setListState]
  );
  const [models, setModels] = useState<any[]>([]);
  const [oems, setOEMs] = useState<any[]>([]);
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  // Remove unused delete modal state since it's handled by parent

  // State for return modal
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [vehicleToReturn, setVehicleToReturn] = useState<Vehicle | null>(null);

  // State for print label modal
  const [vehicleToPrint, setVehicleToPrint] = useState<Vehicle | null>(null);

  // Filter configuration for UniversalFilter
  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-2-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Model',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: any) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-information-line',
        multiple: true,
        staticOptions: [
          { key: VehicleStatus.IDLE, label: 'Idle', value: VehicleStatus.IDLE },
          {
            key: VehicleStatus.ACTIVE,
            label: 'Active',
            value: VehicleStatus.ACTIVE,
          },
          { key: VehicleStatus.LOST, label: 'Lost', value: VehicleStatus.LOST },
          {
            key: VehicleStatus.IN_REPAIR,
            label: 'In Repair',
            value: VehicleStatus.IN_REPAIR,
          },
          {
            key: VehicleStatus.BROKEN,
            label: 'Broken',
            value: VehicleStatus.BROKEN,
          },
        ],
      },
      {
        key: 'contractTypes',
        title: 'Contract Type',
        icon: 'ri-file-list-3-line',
        multiple: true,
        staticOptions: [
          { key: 'RTO', label: 'Rent to Own', value: 'RTO' },
          { key: 'RENT', label: 'Rent', value: 'RENT' },
        ],
      },
      {
        key: 'hubIDs',
        title: 'Hub Location',
        icon: 'ri-map-pin-line',
        multiple: true,
        dataSource: hubs,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        staticOptions: BUSINESS_UNIT_OPTIONS,
      },
      {
        key: 'purchaseOrderIDs',
        title: 'Purchase Order ID',
        icon: 'ri-shopping-cart-2-line',
        multiple: true,
        dataSource: purchaseOrders,
        labelField: 'code',
        valueField: 'ID',
      },
    ],
    [oems, models, hubs, purchaseOrders]
  );

  // API integration
  const fetchVehicles = useCallback(
    async (
      page: number,
      search?: string,
      filters?: Record<string, any>,
      size?: number,
      range?: DateRange
    ) => {
      try {
        setLoading(true);

        // Convert filters for API - ensure arrays are comma-separated strings
        const apiFilters = { ...filters };
        if (apiFilters?.statuses && Array.isArray(apiFilters.statuses)) {
          apiFilters.statuses = apiFilters.statuses.join(',');
        }
        if (apiFilters?.hubIDs && Array.isArray(apiFilters.hubIDs)) {
          apiFilters.hubIDs = apiFilters.hubIDs.join(',');
        }
        if (
          apiFilters?.businessUnits &&
          Array.isArray(apiFilters.businessUnits)
        ) {
          apiFilters.businessUnits = apiFilters.businessUnits.join(',');
        }

        // We leave contractTypes in apiFilters so it's sent to the backend.
        // If the backend doesn't support it, it ignores it.

        // Add date range filters if provided
        if (range?.from) {
          const year = range.from.getFullYear();
          const month = String(range.from.getMonth() + 1).padStart(2, '0');
          const day = String(range.from.getDate()).padStart(2, '0');
          apiFilters.contractStartDate = `${year}-${month}-${day}`;
        }
        if (range?.to) {
          const year = range.to.getFullYear();
          const month = String(range.to.getMonth() + 1).padStart(2, '0');
          const day = String(range.to.getDate()).padStart(2, '0');
          apiFilters.contractEndDate = `${year}-${month}-${day}`;
        }

        const response = await vehiclesApi.getVehicles(
          page,
          size || itemsPerPage,
          search,
          apiFilters
        );

        setVehicles(response.data || []);
        const total = response.pagination?.total || 0;
        setTotalItems(total);

        // Notify parent component of the total count
        if (onTotalChange) {
          onTotalChange(total);
        }
      } catch (err) {
        const errorMessage = extractApiErrorMessage(
          err,
          'Failed to fetch vehicles'
        );
        showErrorToast('Error', errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage, onTotalChange]
  );

  // Card click handler with multiple card support + Total Vehicles special behavior
  const handleCardClick = useCallback(
    (metric: VehicleMetric) => {
      if (metric === VehicleMetric.TOTAL) {
        // Special behavior for Total Vehicles: Reset to default state
        setActiveCards(new Set([VehicleMetric.TOTAL]));
        setActiveFilters({ statuses: DEFAULT_STATUSES.split(',') });
        return;
      }

      // Regular status filter behavior
      const newActiveCards = new Set(activeCards);

      // Remove TOTAL if it was selected (mutually exclusive with specific statuses)
      newActiveCards.delete(VehicleMetric.TOTAL);

      // Toggle the clicked metric
      if (activeCards.has(metric)) {
        newActiveCards.delete(metric);
      } else {
        newActiveCards.add(metric);
      }

      // Convert active cards to status filters
      const statusFilters = Array.from(newActiveCards)
        .map((card) => VEHICLE_METRIC_TO_STATUS_MAP[card])
        .filter((status) => status !== null);

      // Replace activeFilters completely with only status filters (don't preserve other filters)
      const newFilters: Record<string, any> = {
        statuses:
          statusFilters.length > 0
            ? statusFilters
            : DEFAULT_STATUSES.split(','),
      };

      // Determine which cards should be active
      const shouldShowTotalCard = statusFilters.length === 0;

      setActiveCards(
        shouldShowTotalCard ? new Set([VehicleMetric.TOTAL]) : newActiveCards
      );
      setActiveFilters(newFilters);
    },
    [activeCards, setActiveCards, setActiveFilters]
  );

  // API dashboard integration
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await Axios.get('/v1/vehicles/dashboards');
      const data = res?.data?.data;
      if (data) {
        setDashboard({
          totalActive: Number(data.totalActive) || 0,
          totalIdle: Number(data.totalIdle) || 0,
          totalInRepair: Number(data.totalInRepair) || 0,
          totalBroken: Number(data.totalBroken) || 0,
          totalLost: Number(data.totalLost) || 0,
          totalIdleDays: Number(data.totalIdleDays) || 0,
          avgIdleDays: Number(data.avgIdleDays) || 0,
          maxIdleDays: Number(data.maxIdleDays) || 0,
          totalIdleCost: Number(data.totalIdleCost) || 0,
        });
      }
    } catch (err) {
      const msg = extractApiErrorMessage(err, 'Failed to fetch dashboard');
      showErrorToast('Error', msg);
    }
  }, []);

  // refresh dashboard on mount & when refreshTrigger changes
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, refreshTrigger]);

  useEffect(() => {
    fetchVehicles(
      currentPage,
      searchTerm,
      activeFilters,
      itemsPerPage,
      dateRange
    );
  }, [
    currentPage,
    searchTerm,
    activeFilters,
    sortField,
    sortDirection,
    fetchVehicles,
    itemsPerPage,
    dateRange,
    refreshTrigger,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, totalItems, loading);

  useEffect(() => {
    if (!onDownloadParamsChange) return;

    const params: Record<string, any> = {};

    const apiFilters = { ...activeFilters };
    if (apiFilters?.statuses && Array.isArray(apiFilters.statuses)) {
      apiFilters.statuses = apiFilters.statuses.join(',');
    }
    if (apiFilters?.oemIDs && Array.isArray(apiFilters.oemIDs)) {
      apiFilters.oemIDs = apiFilters.oemIDs.join(',');
    }
    if (apiFilters?.modelIDs && Array.isArray(apiFilters.modelIDs)) {
      apiFilters.modelIDs = apiFilters.modelIDs.join(',');
    }
    if (apiFilters?.contractTypes && Array.isArray(apiFilters.contractTypes)) {
      apiFilters.contractTypes = apiFilters.contractTypes.join(',');
    }
    if (apiFilters?.hubIDs && Array.isArray(apiFilters.hubIDs)) {
      apiFilters.hubIDs = apiFilters.hubIDs.join(',');
    }
    if (apiFilters?.businessUnits && Array.isArray(apiFilters.businessUnits)) {
      apiFilters.businessUnits = apiFilters.businessUnits.join(',');
    }

    if (dateRange?.from) {
      const year = dateRange.from.getFullYear();
      const month = String(dateRange.from.getMonth() + 1).padStart(2, '0');
      const day = String(dateRange.from.getDate()).padStart(2, '0');
      apiFilters.contractStartDate = `${year}-${month}-${day}`;
    }
    if (dateRange?.to) {
      const year = dateRange.to.getFullYear();
      const month = String(dateRange.to.getMonth() + 1).padStart(2, '0');
      const day = String(dateRange.to.getDate()).padStart(2, '0');
      apiFilters.contractEndDate = `${year}-${month}-${day}`;
    }

    onDownloadParamsChange({ ...params, ...apiFilters });
  }, [
    onDownloadParamsChange,
    currentPage,
    itemsPerPage,
    searchTerm,
    activeFilters,
    dateRange,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [actionDropdownOpen]);

  useEffect(() => {
    fetchModels();
    fetchOEMs();
    fetchHubs();
    fetchPurchaseOrdersList();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await getListModel({ page: '1', size: '1000' });
      setModels(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to fetch models'
      );
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchOEMs = async () => {
    try {
      const response = await getListOem({ page: '1', size: '1000' });
      setOEMs(response.data);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err, 'Failed to fetch OEMs');
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await getListPitstop({
        page: '1',
        size: '10000',
        type: 'HUB',
        hasStaff: true,
      });
      setHubs(response.data || []);
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err, 'Failed to fetch hubs');
      showErrorToast('Error', errorMessage);
    }
  };

  const fetchPurchaseOrdersList = async () => {
    try {
      const response = await getPurchaseOrders({ page: 1, size: 1000 });
      setPurchaseOrders(response.data || []);
    } catch (err) {
      // Non-blocking: the PO filter just stays empty if this fails
    }
  };

  const sortedVehicles = useMemo(() => {
    const sorted = [...vehicles].sort((a, b) => {
      let aValue: any = a[sortField as keyof typeof a];
      let bValue: any = b[sortField as keyof typeof b];

      // Special handling for contractType which lives in the model
      if (sortField === ('contractType' as any)) {
        const modelA = models.find((m) => String(m.ID) === String(a.modelID));
        const modelB = models.find((m) => String(m.ID) === String(b.modelID));
        aValue = modelA?.contractType || '';
        bValue = modelB?.contractType || '';
      }

      if (sortField === 'createdAt' || sortField === 'updatedAt') {
        const aDate = aValue ? new Date(aValue as string).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string).getTime() : 0;
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }, [vehicles, sortField, sortDirection, models]);

  const handleSort = (field: string, direction?: 'asc' | 'desc' | null) =>
    setSort(
      field,
      nextSortDirection(
        field,
        { field: sortField, direction: sortDirection },
        direction
      )
    );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleEditVehicle = useCallback(
    (vehicle: Vehicle) => {
      if (onEditVehicle) {
        onEditVehicle(vehicle);
      }
      setActionDropdownOpen(null);
    },
    [onEditVehicle]
  );

  const handleReturnVehicle = useCallback((vehicle: Vehicle) => {
    setVehicleToReturn(vehicle);
    setIsReturnModalOpen(true);
    setActionDropdownOpen(null);
  }, []);

  const handleReturnSuccess = () => {
    // Refresh the vehicle list after successful return
    fetchVehicles(
      currentPage,
      searchTerm,
      activeFilters,
      itemsPerPage,
      dateRange
    );
    fetchDashboard();
  };

  // Helper function to create action dropdown actions for vehicles
  const createVehicleActions = useCallback(
    (vehicle: Vehicle): ActionDropdownAction[] => {
      const actions: ActionDropdownAction[] = [
        {
          key: 'view',
          label: 'View Detail',
          icon: 'ri-eye-line',
          onClick: () => navigate(`/vehicles/${vehicle.ID || vehicle.id}`),
        },
        {
          key: 'edit',
          label: 'Edit',
          icon: 'ri-pencil-line',
          onClick: () => handleEditVehicle(vehicle),
          separateAfter: true,
        },
      ];

      // Only show "Return" action for vehicles with status IDLE or BROKEN
      if (
        vehicle.status === VehicleStatus.IDLE ||
        vehicle.status === VehicleStatus.BROKEN
      ) {
        actions.push({
          key: 'return',
          label: 'Return',
          icon: 'ri-arrow-go-back-line',
          onClick: () => handleReturnVehicle(vehicle),
        });
      }

      return actions;
    },
    [handleEditVehicle, handleReturnVehicle, navigate]
  );

  // Memoized toggle dropdown handler to prevent function recreation
  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const columns = [
    {
      key: 'purchaseOrderCode',
      title: 'Purchase Order ID',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            value
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'platNomor',
      title: 'Plate Number',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-bold">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'arriveAt',
      title: 'Tanggal & Waktu Kedatangan',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            dayjs(value).format('DD/MM/YYYY, HH:mm')
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'oemName',
      title: 'OEM Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'modelName',
      title: 'Model Name',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'hubName',
      title: 'Hub Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'businessUnit',
      title: 'Business Unit',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? (
            getBusinessUnitLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'entity',
      title: 'Entity',
      sortable: false,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            getCompanyEntityLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },

    {
      key: 'vin',
      title: 'Nomor Rangka/VIN',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'imei',
      title: 'Nomor Mesin',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            value
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },

    {
      key: 'contractType',
      title: 'Contract Type',
      sortable: true,
      render: (_value: any, record: Vehicle) => {
        const model = models.find(
          (m) => String(m.ID) === String(record.modelID)
        );
        const type = model?.contractType;
        if (!type) {
          return <span className="text-gray-400">-</span>;
        }
        return <Status value={type} type="contract" />;
      },
    },
    {
      key: 'contractDuration',
      title: 'Contract Duration',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value != null ? (
            value + ' Months'
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'contractStartDate',
      title: 'Contract Start Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            dayjs(value).format('DD/MM/YYYY')
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'contractEndDate',
      title: 'Contract End Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? (
            dayjs(value).format('DD/MM/YYYY')
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'color',
      title: 'Color',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: VehicleStatus) => (
        <Status value={statusLabelMapper[value] || value} type="feedback" />
      ),
    },
    {
      // Idle Days / Idle Cost: only IDLE vehicles carry a value; every other
      // status renders an em dash "—" (not 0) to signal "not applicable".
      // Sorting is deferred (computed field, needs server-side ORDER BY).
      key: 'idleDays',
      title: 'Idle Days',
      titleBadge: 'NEW',
      sortable: false,
      render: (value: number | null) =>
        value != null ? (
          <span className="text-[14px] font-semibold text-amber-700">
            {value} days
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'idleCost',
      title: 'Idle Cost',
      titleBadge: 'NEW',
      sortable: false,
      // Settled units (OEM deal paid off) idle with no cost: "—" + badge so
      // they're distinguishable from plain non-idle dashes.
      render: (value: number | null, record: Vehicle) =>
        value != null ? (
          <span className="text-[14px] font-semibold text-amber-700">
            {formatRupiah(value)}
          </span>
        ) : record.idleDays != null && record.costSettledAt != null ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-gray-400">—</span>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              Settled
            </span>
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'stnkLink',
      title: 'STNK Link',
      sortable: false,
      render: (value: string) => {
        if (!value) return '-';
        const url = value.startsWith('http') ? value : `https://${value}`;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[14px] text-blue-600 hover:text-blue-800 underline"
          >
            {value.startsWith('http') ? value : `https://${value}`}
          </a>
        );
      },
    },
    {
      key: 'appUsername',
      title: 'App Username',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'appPassword',
      title: 'App Password',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-24',
      align: 'center' as const,
      render: (value: any, record: Vehicle, index?: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        return (
          <div
            className="flex items-center justify-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setVehicleToPrint(record)}
              className="p-1 hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Print Label"
              aria-label={`Print label for vehicle ${record.platNomor}`}
            >
              <i className="ri-printer-line text-gray-500 text-lg"></i>
            </button>
            <ActionDropdown
              record={record}
              isOpen={actionDropdownOpen === uniqueId}
              onToggle={() => handleToggleDropdown(uniqueId)}
              actions={createVehicleActions(record)}
              ariaLabel={`Actions for vehicle ${record.platNomor}`}
            />
          </div>
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Vehicle>({
    storageKey: 'vehicles-owned',
    columns,
  });

  return (
    <>
      {/* Summary cards with click-to-filter functionality */}
      <VehiclesSummaryCards
        dashboard={dashboard}
        activeCards={activeCards}
        onCardClick={handleCardClick}
      />

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              key={controlsKey}
              onDateRangeChange={setDateRange}
              {...datePickerSeed}
            />
          </div>
          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={
                // Hide default statuses from showing as active filter in UI
                // Check if statuses equals default (either as string or array)
                activeFilters.statuses === DEFAULT_STATUSES ||
                (Array.isArray(activeFilters.statuses) &&
                  activeFilters.statuses.join(',') === DEFAULT_STATUSES)
                  ? { ...activeFilters, statuses: undefined }
                  : activeFilters
              }
              onApply={(filters: Record<string, any>) => {
                const finalFilters = { ...filters };
                if (!filters.statuses || filters.statuses.length === 0) {
                  finalFilters.statuses = DEFAULT_STATUSES.split(',');
                }
                // Ensure statuses is always an array for internal use
                if (typeof finalFilters.statuses === 'string') {
                  finalFilters.statuses = finalFilters.statuses.split(',');
                }

                setActiveFilters(finalFilters);

                const statusFilters = Array.isArray(filters.statuses)
                  ? filters.statuses
                  : [];

                const hasAnyFilters = Object.keys(filters).length > 0;

                if (statusFilters.length > 0) {
                  const newActiveCards = new Set<VehicleMetric>();
                  statusFilters.forEach((status: VehicleStatus) => {
                    const correspondingMetric =
                      VEHICLE_STATUS_TO_METRIC_MAP[status];
                    if (correspondingMetric) {
                      newActiveCards.add(correspondingMetric);
                    }
                  });
                  setActiveCards(newActiveCards);
                } else if (hasAnyFilters) {
                  // If there are filters (OEM/Model) but no status filters, don't highlight Total
                  setActiveCards(new Set());
                } else {
                  // Only highlight Total Vehicles if there are no filters at all
                  setActiveCards(new Set([VehicleMetric.TOTAL]));
                }
              }}
              onClear={() => {
                setActiveFilters({
                  statuses: DEFAULT_STATUSES.split(','),
                });
                setCurrentPage(1);
                setActiveCards(new Set([VehicleMetric.TOTAL]));
              }}
            />
            <ResetFiltersButton
              // The cards and the default statuses are the unfiltered view.
              visible={hasFilters()}
              onReset={() => resetFilters()}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading Vehicles...</span>
            </div>
          ) : (
            <TableData
              data={sortedVehicles}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              onRowClick={(vehicle) =>
                navigate(`/vehicles/${vehicle.ID || vehicle.id}`)
              }
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setItemsPerPage(newPageSize);
          fetchVehicles(1, searchTerm, activeFilters, newPageSize, dateRange);
        }}
      />

      {/* Print Label Modal */}
      <PrintLabelModal
        isOpen={vehicleToPrint !== null}
        onClose={() => setVehicleToPrint(null)}
        qrValue={String(vehicleToPrint?.ID ?? vehicleToPrint?.id ?? '')}
        labelText={vehicleToPrint?.platNomor || '-'}
      />

      {/* Return Vehicle Modal */}
      <ReturnVehicleModal
        isOpen={isReturnModalOpen}
        vehicle={vehicleToReturn}
        isEditMode={false}
        onClose={() => {
          setIsReturnModalOpen(false);
          setVehicleToReturn(null);
        }}
        onSuccess={handleReturnSuccess}
      />
    </>
  );
};

export default OwnedVehicleTab;
